import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../../../api/HttpClient';
import LandingPageHeader from '../../../components/LandingPageHeader/LandingPageHeader';

type Step = 'phone' | 'otp' | 'password';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!phoneNumber.trim()) { setError('Please enter your phone number.'); return; }
    setLoading(true);
    try {
      const client = await apiClient;
      await client.sendLoginOtp(phoneNumber.trim());
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!otp.trim() || otp.length !== 6) { setError('Please enter the 6-digit OTP.'); return; }
    setStep('password');
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const client = await apiClient;
      await client.resetPasswordByOtp(phoneNumber.trim(), otp.trim(), newPassword, confirmPassword);
      navigate('/login', { state: { message: 'Password reset successfully. Please log in.' } });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepTitles: Record<Step, string> = {
    phone: 'Forgot Your Password?',
    otp: 'Enter OTP',
    password: 'Set New Password',
  };

  const stepDescriptions: Record<Step, string> = {
    phone: "Enter your registered phone number. We'll send an OTP via WhatsApp.",
    otp: `Enter the 6-digit OTP sent to ${phoneNumber} via WhatsApp.`,
    password: 'Choose a new password for your account.',
  };

  const inputClass =
    'appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-deep-emerald-green focus:border-deep-emerald-green sm:text-sm';

  const btnClass =
    'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-deep-emerald-green hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-deep-emerald-green disabled:opacity-60';

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <LandingPageHeader isMobileMenuOpen={false} setIsMobileMenuOpen={() => {}} />
      <div className="flex-grow flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 p-10 bg-white rounded-xl shadow-lg">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {stepTitles[step]}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {stepDescriptions[step]}
            </p>
          </div>

          {step === 'phone' && (
            <form className="mt-8 space-y-6" onSubmit={handleSendOtp}>
              <input
                type="tel"
                required
                className={inputClass}
                placeholder="Phone number (e.g. +919876543210)"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              {error && <p className="text-center text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={loading} className={btnClass}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form className="mt-8 space-y-6" onSubmit={handleVerifyOtp}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                className={`${inputClass} text-center tracking-widest text-lg`}
                placeholder="------"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              />
              {error && <p className="text-center text-sm text-red-600">{error}</p>}
              <button type="submit" className={btnClass}>Verify OTP</button>
              <p className="text-center text-sm text-gray-500">
                Didn't receive it?{' '}
                <button
                  type="button"
                  className="text-deep-emerald-green hover:underline"
                  onClick={() => { setStep('phone'); setError(''); }}
                >
                  Resend
                </button>
              </p>
            </form>
          )}

          {step === 'password' && (
            <form className="mt-8 space-y-4" onSubmit={handleResetPassword}>
              <input
                type="password"
                required
                className={inputClass}
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                type="password"
                required
                className={inputClass}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {error && <p className="text-center text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={loading} className={btnClass}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <div className="text-center">
            <Link to="/login" className="font-medium text-deep-emerald-green hover:text-emerald-700">
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
